import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  Button,
  Modal,
  Image,
  Dropdown,
  Nav,
  Tab,
} from "react-bootstrap";
import React, { useState, useEffect, useCallback } from "react";
import { FaCog, FaQuestionCircle, FaStar } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/CreditDashboard.css";
import { accountService } from '../services/accountService';
import { userService } from '../services/userService';
import { domainService } from '../services/domainService';
import { FaCheckCircle } from "react-icons/fa";
import searchIcon from "../assets/images/icons/search.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import { GrEdit } from "react-icons/gr";
import { FaEllipsisV } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import CompanySidebar from "../components/CompanySidebar";

import noRecordFound from "../assets/images/no-record.png";
import companyImage from "../assets/images/icons/CompanyIcon.png";
import { faPencil } from "@fortawesome/free-solid-svg-icons";

const CompanyManagement = ({ embedded = false, selectedCompanyId: selectedCompanyIdProp = null }) => {
  const getUserStatus = (user) => {
    return user && user.enabled ? "Active" : "Inactive";
  };

  const [selectedCompanyId, setSelectedCompanyId] = useState(selectedCompanyIdProp);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  // Track whether we have already tried fetching users for a companyId (prevents refetch loops on 404)
  const [triedFetchUsersFor, setTriedFetchUsersFor] = useState({});

  const [companyLogos, setCompanyLogos] = useState({});

  const fetchCompanyLogo = async (fileName) => {
    if (!fileName) {
      return null;
    }
    
    try {
      const blob = await accountService.downloadLogo(fileName);
      
      if (blob && blob.size > 0) {
        
        // Ensure we have a proper MIME type for the blob
        let mimeType = blob.type;
        if (!mimeType || mimeType === 'application/octet-stream') {
          // If no MIME type is detected, assume it's a PNG image
          mimeType = 'image/png';
        }
        
        // Convert blob to base64 data URL with proper MIME type
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            let dataUrl = reader.result;
            
            // Ensure the data URL has the correct format: data:image/png;base64,...
            if (dataUrl && !dataUrl.startsWith('data:image/')) {
              // If the data URL doesn't have the proper format, reconstruct it
              const base64Data = dataUrl.split(',')[1] || dataUrl;
              dataUrl = `data:${mimeType};base64,${base64Data}`;
            }
            
            resolve(dataUrl);
          };
          reader.onerror = () => {
            console.error('Error reading blob as data URL in CompanyManagement');
            reject(reader.error);
          };
          reader.readAsDataURL(blob);
        });
      }
      return null;
    } catch (error) {
      console.error("Error fetching company logo in CompanyManagement:", error);
      return null;
    }
  };

  const fetchCompanyUsers = useCallback(async (companyId) => {
    if (!companyId) {
      setCompanyUsers([]);
      setDataFetched(true);
      setTriedFetchUsersFor((prev) => ({ ...prev, [companyId]: true }));
      return;
    }

    try {
      // Mark as tried immediately to avoid concurrent refetches from effects
      setTriedFetchUsersFor((prev) => ({ ...prev, [companyId]: true }));
      setIsLoading(true);
      setDataFetched(false);
      const response = await userService.getUsersByAccount(companyId);

      if (Array.isArray(response)) {
        setCompanyUsers(response);
        setTriedFetchUsersFor((prev) => ({ ...prev, [companyId]: true }));
      } else if (response && response.content && Array.isArray(response.content)) {
        // Handle paginated response format
        setCompanyUsers(response.content);
        setTriedFetchUsersFor((prev) => ({ ...prev, [companyId]: true }));
      } else {
        console.warn("Unexpected user data format:", response);
        setCompanyUsers([]);
        setTriedFetchUsersFor((prev) => ({ ...prev, [companyId]: true }));
      }
    } catch (err) {
      console.error("Failed to fetch company users:", err);
      if (err.response && err.response.status === 404) {
        setCompanyUsers([]);
      } else {
        setCompanyUsers([]);
      }
    } finally {
      setIsLoading(false);
      setDataFetched(true);
      setTriedFetchUsersFor((prev) => ({ ...prev, [companyId]: true }));
    }
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoading(true);
        const response = await accountService.getAccountConfigs();
        if (response && response.length > 0) {
          const companiesData = response;
          setCompanies(companiesData);

          const logoPromises = companiesData.map(async (company) => {
            if (company.logo) {
              const logoUrl = await fetchCompanyLogo(company.logo);
              return [company.id, logoUrl];
            }
            return [company.id, null];
          });

          const logos = Object.fromEntries(await Promise.all(logoPromises));
          setCompanyLogos(logos);

          const initialCompanyId = selectedCompanyIdProp || companiesData[0].id;
          setSelectedCompanyId((prev) => prev ?? initialCompanyId);
          await fetchCompanyUsers(initialCompanyId);
          await fetchCompanySummary(initialCompanyId);
          await fetchCompanyDetails(initialCompanyId);
        }
      } catch (err) {
        console.error("Failed to fetch companies:", err);
        toast.error("Failed to fetch companies");
      } finally {
        setIsLoading(false);
        setDataFetched(true);
      }
    };

    fetchCompanies();
  }, [fetchCompanyUsers]);

  useEffect(() => {
    if (selectedCompanyIdProp) {
      setSelectedCompanyId(selectedCompanyIdProp);
    }
  }, [selectedCompanyIdProp]);

  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({
    accountName: "",
    domains: [],
    defaultDomain: "",
    senderIds: [],
    isActive: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 5;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    id: "",
    accountName: "",
    domains: [],
    defaultDomain: "",
    senderIds: [],
    isActive: true,
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = (currentPage - 1) * recordsPerPage;
  const currentRecords = companyUsers.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(companyUsers.length / recordsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleCreateCompany = async () => {
    try {
      const formattedData = {
        ...newCompanyData,
        domains: Array.isArray(newCompanyData.domains)
          ? newCompanyData.domains
          : [newCompanyData.domains],
      };

      await accountService.createAccount(formattedData);

      setShowCreateCompanyModal(false);
      toast.success("Company added successfully");
      fetchCompanyRecords();
      setNewCompanyData({
        accountName: "",
        domains: [],
        defaultDomain: "",
        senderIds: [],
        isActive: true,
      });
    } catch (error) {
      console.error("Error adding company:", error);
    }
  };

  const fetchCompanyRecords = async () => {
    try {
      setLoading(true);
      const response = await accountService.getAllAccountConfigs();

      if (!response) {
        throw new Error("No data received");
      }
      setRecords(response);
    } catch (error) {
      console.error("Error fetching company records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      try {
        const decoded = jwtDecode(storedToken);
        setUserRole(decoded?.roles?.[0]);
      } catch (err) {
        console.error("Token decoding failed", err);
        toast.error("Invalid token, please login again");
      }
    } else {
      toast.error("Please login to access this page");
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchCompanyRecords();
    }
  }, [token]);


  const confirmDelete = async () => {
    try {
      await accountService.deleteAccountConfig(companyToDelete.id);

      setShowDeleteModal(false);
      setCompanyToDelete(null);
      fetchCompanyRecords();
      toast.success("Company deleted successfully");
    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error("Failed to delete company");
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "domains") {
      const domainsArray = value
        ? value.split(",").map((domain) => domain.trim())
        : [];
      setEditFormData({
        ...editFormData,
        domains: domainsArray,
      });
    } else if (name === "senderIds") {
      const senderIdsArray = value
        ? value.split(",").map((id) => id.trim())
        : [];
      setEditFormData({
        ...editFormData,
        senderIds: senderIdsArray,
      });
    } else {
      setEditFormData({
        ...editFormData,
        [name]: name === "isActive" ? value === "true" : value,
      });
    }
  };

  const saveEditChanges = async () => {
    try {
      const formattedData = {
        ...editFormData,
        domains: Array.isArray(editFormData.domains)
          ? editFormData.domains
          : [editFormData.domains],
      };


      await accountService.updateAccountConfig(formattedData);

      setShowEditModal(false);
      setCompanyToEdit(null);
      // fetchCompanyRecords();
      toast.success("Company updated successfully");
    } catch (error) {
      console.error("Error updating company:", error);
      toast.error("Failed to update company");
    }
  };

  const [activeTab, setActiveTab] = useState("users");
  const [showUsersTable, setShowUsersTable] = useState(true);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };
  useEffect(() => {
    if (activeTab === "senderId") {
      fetchSenderIds(selectedCompanyId);
    } else if (activeTab === "domain") {
      fetchDomains(selectedCompanyId);
    }
  }, [activeTab, selectedCompanyId]);


  const [summaryData, setSummaryData] = useState({
    userCount: 0,
    domainCount: 0,
    senderIdCount: 0,
    availableCredit: [
      { shortening: 0 },
      { tracking: 0 }
    ],
    usedCredit: [
      { shortening: 0 },
      { tracking: 0 }
    ]
  });

  const fetchCompanySummary = async (accountId) => {
    try {
      const response = await accountService.getAccountSummary(accountId);
      setSummaryData(response);
    } catch (error) {
      console.error("Failed to fetch company summary:", error);
    }
  };

  const [companyDetails, setCompanyDetails] = useState({
    accountName: "",
    language: "",
    logo: "",
    location: "",
    domains: [],
    defaultDomain: "",
    senderIds: [],
    isActive: true,
  });

  const fetchCompanyDetails = async (accountId) => {
    try {
      const response = await accountService.getAccountConfigById(accountId);
      setCompanyDetails(response);
    } catch (error) {
      console.error("Failed to fetch company details:", error);
    }
  };
  useEffect(() => {
    if (!selectedCompanyId || showEditModal || showDeleteModal) return;

    const fetchData = async () => {
      try {
        if (!dataFetched) {
          setIsLoading(true);
        }
        
        await Promise.all([
          fetchCompanySummary(selectedCompanyId),
          fetchCompanyDetails(selectedCompanyId),
        ]);

        // Avoid refetch loops when there are no users (e.g., backend returns 404)
        // Only fetch users once per company selection; if already tried, don't refetch on empty/mismatch
        const needUsersForSelected = !triedFetchUsersFor[selectedCompanyId] && (companyUsers.length === 0 || companyUsers[0]?.accountId !== selectedCompanyId);
        if (needUsersForSelected) {
          await fetchCompanyUsers(selectedCompanyId);
        }
      } catch (error) {
        console.error("Error fetching company data", error);
        toast.error("Failed to fetch company data");
      } finally {
        setIsLoading(false);
        setDataFetched(true);
      }
    };

    fetchData();
  }, [selectedCompanyId, showEditModal, showDeleteModal, companyUsers, dataFetched, fetchCompanyUsers, triedFetchUsersFor]);

  const [senderIds, setSenderIds] = useState([]);
  const [showSenderIdEditModal, setShowSenderIdEditModal] = useState(false);
  const [showSenderIdDeleteModal, setShowSenderIdDeleteModal] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [senderIdEditForm, setSenderIdEditForm] = useState({
    senderId: "",
    isApproved: true,
  });

  const fetchSenderIds = async (accountId) => {
    if (!accountId) {
      console.warn("No account ID provided to fetchSenderIds");
      return;
    }

    try {
      const response = await accountService.getSenderIdsByAccount(accountId);


      if (response) {
        setSenderIds(response);
      } else {
        toast.error("Sender ID fetch failed: invalid response format");
      }
    } catch (error) {
      console.error(
        "Failed to fetch sender IDs:",
        error.response?.data || error.message
      );
    }
  };

  const handleSenderIdEdit = async () => {
    try {
      const error = validateSenderId(senderIdEditForm.senderId);
      if (error) {
        setSenderIdEditError(error);
        return;
      }

      setSenderIdEditError("");
      const response = await accountService.updateSenderId(selectedSenderId.id, { senderId: senderIdEditForm.senderId });


      setSenderIds((prevSenderIds) =>
        prevSenderIds.map((sender) =>
          sender.id === selectedSenderId.id
            ? { ...sender, senderId: senderIdEditForm.senderId }
            : sender
        )
      );

      toast.success("Sender ID updated successfully");
      setShowSenderIdEditModal(false);
      setSelectedSenderId(null);
      setSenderIdEditError("");

      await fetchSenderIds(selectedCompanyId);
    } catch (error) {
      console.error("Failed to update sender ID:", error);
      setSenderIdEditError(error.response?.data?.message || "Failed to update sender ID");
    }
  };

  const handleSenderIdDelete = async () => {
    try {
      await accountService.deleteSenderId(selectedSenderId.id);

      setSenderIds((prevSenderIds) =>
        prevSenderIds.filter((sender) => sender.id !== selectedSenderId.id)
      );

      setSummaryData((prev) => ({
        ...prev,
        senderIdCount: prev.senderIdCount - 1,
      }));

      toast.success("Sender ID deleted successfully");
      setShowSenderIdDeleteModal(false);
      setSelectedSenderId(null);
    } catch (error) {
      console.error("Failed to delete sender ID:", error);
      toast.error("Failed to delete sender ID");
    }
  };

  const [domains, setDomains] = useState([]);
  const [showDomainEditModal, setShowDomainEditModal] = useState(false);
  const [showDomainDeleteModal, setShowDomainDeleteModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainEditForm, setDomainEditForm] = useState({
    domain: "",
    isVerified: true,
  });

  const fetchDomains = async (accountId) => {
    if (!accountId) {
      console.warn("fetchDomains called with invalid accountId:", accountId);
      return;
    }

    try {
      const response = await accountService.getDomainsByAccount(accountId);

      if (response) {
        setDomains(response);
      } else {
        console.warning("Unexpected response format while fetching domains");
      }
    } catch (error) {
      console.error("Failed to fetch domains:", error);
    }
  };
  useEffect(() => {
    fetchDomains("your-working-account-id-here");
  }, []);

  const handleDomainEdit = async () => {
    try {
      // Validate domain (assuming it already has protocol)
      if (!domainEditForm.domain.trim()) {
        setDomainEditError("Domain is required");
        return;
      }
      if (domainEditForm.domain.length > 50) {
        setDomainEditError("Domain must not exceed 50 characters");
        return;
      }

      setDomainEditError("");
      await accountService.updateDomain(selectedDomain.id, { domain: domainEditForm.domain });

      setDomains((prevDomains) =>
        prevDomains.map((domain) =>
          domain.id === selectedDomain.id
            ? { ...domain, domain: domainEditForm.domain }
            : domain
        )
      );

      toast.success("Domain updated successfully");
      setShowDomainEditModal(false);
      setSelectedDomain(null);
      setDomainEditError("");
    } catch (error) {
      console.error("Failed to update domain:", error);
      setDomainEditError(error.response?.data?.message || "Failed to update domain");
    }
  };

  const handleDomainDelete = async () => {
    try {
      await accountService.deleteDomain(selectedDomain.id);

      setDomains((prevDomains) =>
        prevDomains.filter((domain) => domain.id !== selectedDomain.id)
      );

      setSummaryData((prev) => ({
        ...prev,
        domainCount: prev.domainCount - 1,
      }));

      toast.success("Domain deleted successfully");
      setShowDomainDeleteModal(false);
      setSelectedDomain(null);
    } catch (error) {
      console.error("Failed to delete domain:", error);
      toast.error("Failed to delete domain");
    }
  };

  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [showAddSenderIdModal, setShowAddSenderIdModal] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newSenderId, setNewSenderId] = useState("");
  const [userRole, setUserRole] = useState("");
  
  // Validation states
  const [domainError, setDomainError] = useState("");
  const [senderIdError, setSenderIdError] = useState("");
  const [userEditErrors, setUserEditErrors] = useState({
    email: "",
    contactNo: ""
  });
  const [senderIdEditError, setSenderIdEditError] = useState("");
  const [domainEditError, setDomainEditError] = useState("");

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Enter a valid email address";
    return "";
  };

  const validateContactNo = (contactNo) => {
    if (!contactNo) return "Contact number is required";
    if (!/^\d{10}$/.test(contactNo)) return "Contact number must be exactly 10 digits";
    return "";
  };

  const validateSenderId = (senderId) => {
    if (!senderId.trim()) return "Sender ID is required";
    if (!/^[A-Za-z0-9]{3,20}$/.test(senderId)) {
      return "Sender ID must be 3-20 characters (letters and numbers only, no symbols)";
    }
    return "";
  };

  const validateDomain = (domain, protocol = 'http://') => {
    if (!domain.trim()) return "Domain is required";
    const fullDomain = protocol + domain;
    if (fullDomain.length > 50) return "Domain must not exceed 50 characters";
    return "";
  };

  const handleAddDomain = async () => {
    try {
      const error = validateDomain(newDomain, selectedProtocol);
      if (error) {
        setDomainError(error);
        return;
      }

      setDomainError("");
      const fullDomain = selectedProtocol + newDomain;
      const response = await accountService.addDomainToAccount(selectedCompanyId, fullDomain);

      if (response) {
        await fetchDomains(selectedCompanyId);

        setSummaryData((prev) => ({
          ...prev,
          domainCount: prev.domainCount + 1,
        }));

        toast.success("Domain added successfully");
        setShowAddDomainModal(false);
        setNewDomain("");
        setSelectedProtocol('http://');
        setDomainError("");
        setShowDomainModal(false);
      }
    } catch (error) {
      console.error("Error adding domain:", error);
      setDomainError(error.response?.data?.message || "Failed to add domain");
    }
  };

  const handleAddSenderId = async () => {
    try {
      const error = validateSenderId(newSenderId);
      if (error) {
        setSenderIdError(error);
        return;
      }

      setSenderIdError("");
      await accountService.addSenderIdToAccount(selectedCompanyId, newSenderId);

      setSenderIds([...senderIds, { id: Date.now(), senderId: newSenderId }]);
      setSummaryData((prev) => ({
        ...prev,
        senderIdCount: prev.senderIdCount + 1,
      }));

      toast.success("Sender ID added successfully");
      setShowAddSenderIdModal(false);
      setNewSenderId("");
      setSenderIdError("");
      fetchSenderIds(selectedCompanyId);
    } catch (error) {
      console.error("Failed to add sender ID:", error);
      setSenderIdError(error.response?.data?.message || "Failed to add sender ID");
    }
  };

  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userEditFormData, setUserEditFormData] = useState({
    contactNo: "",
    email: "",
    role: "",
  });
  const availableRoles = ["USER", "ADMIN"];

  const handleUserEditClick = (user) => {
    setUserToEdit(user);
    setUserEditFormData({
      contactNo: user.contactNo,
      email: user.email,
      role: user.roles ? user.roles[0] : "",
    });
    setShowUserEditModal(true);
  };

  const handleUserDeleteClick = (user) => {
    setUserToDelete(user);
    setShowUserDeleteModal(true);
  };

  const handleUserEditFormChange = (e) => {
    const { name, value } = e.target;
    setUserEditFormData({
      ...userEditFormData,
      [name]: value,
    });
    
    // Validate on change
    if (name === 'email') {
      setUserEditErrors(prev => ({ ...prev, email: validateEmail(value) }));
    } else if (name === 'contactNo') {
      // Only allow digits
      const digitsOnly = value.replace(/[^0-9]/g, '');
      setUserEditFormData({
        ...userEditFormData,
        [name]: digitsOnly,
      });
      setUserEditErrors(prev => ({ ...prev, contactNo: validateContactNo(digitsOnly) }));
    }
  };

  const saveUserEditChanges = async () => {
    try {
      // Validate before saving
      const errors = {
        email: validateEmail(userEditFormData.email),
        contactNo: validateContactNo(userEditFormData.contactNo)
      };
      
      setUserEditErrors(errors);
      
      if (errors.email || errors.contactNo) {
        toast.error("Please fix validation errors before saving");
        return;
      }

      await userService.updateRegisteredUser(userToEdit.id, {
        email: userEditFormData.email,
        contactNo: userEditFormData.contactNo,
        roles: [userEditFormData.role],
      });

      setCompanyUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userToEdit.id
            ? {
                ...user,
                email: userEditFormData.email,
                contactNo: userEditFormData.contactNo,
                roles: [userEditFormData.role],
              }
            : user
        )
      );

      setSummaryData((prev) => ({
        ...prev,
        userCount: prev.userCount,
      }));

      setShowUserEditModal(false);
      setUserToEdit(null);
      setUserEditErrors({ email: "", contactNo: "" });
      toast.success("User updated successfully");
    } catch (err) {
      console.error("Failed to update user:", err);
      toast.error("Failed to update user");
    }
  };

  const confirmUserDelete = async () => {
    try {
      await userService.deleteUser(userToDelete.id);

      setCompanyUsers((prevUsers) =>
        prevUsers.filter((user) => user.id !== userToDelete.id)
      );


      setSummaryData((prev) => ({
        ...prev,
        userCount: prev.userCount - 1,
      }));

      setShowUserDeleteModal(false);
      setUserToDelete(null);
      toast.success("User deleted successfully");
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error("Failed to delete user");
    }
  };

  // useEffect(() => {
  //   const fetchLatestData = async () => {
  //     try {
  //       const token = localStorage.getItem("token");
  //       const response = await axios.get(
  //         `${USER_URL}/api/account-configs/getAll`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );

  //       if (response.data) {
  //         const currentRecordsStr = JSON.stringify(records);
  //         const newRecordsStr = JSON.stringify(response.data);

  //         if (currentRecordsStr !== newRecordsStr) {
  //           setRecords(response.data);

  //           const companiesResponse = await axios.get(
  //             `${USER_URL}/api/account-configs/account`,
  //             {
  //               headers: {
  //                 Authorization: `Bearer ${token}`,
  //                 "Content-Type": "application/json",
  //               },
  //             }
  //           );

  //           if (companiesResponse.data) {
  //             setCompanies(companiesResponse.data);
  //           }
  //         }
  //       }
  //     } catch (error) {
  //       console.error("Error fetching latest data:", error);
  //     }
  //   };

  //   const interval = setInterval(fetchLatestData, 2000);

  //   return () => clearInterval(interval);
  // }, [records]);

  const [senderIdCurrentPage, setSenderIdCurrentPage] = useState(1);
  const [domainCurrentPage, setDomainCurrentPage] = useState(1);

  const handleSenderIdPageChange = (pageNumber) => {
    setSenderIdCurrentPage(pageNumber);
  };

  const handleDomainPageChange = (pageNumber) => {
    setDomainCurrentPage(pageNumber);
  };

  const senderIdIndexOfLastRecord = senderIdCurrentPage * recordsPerPage;
  const senderIdIndexOfFirstRecord = (senderIdCurrentPage - 1) * recordsPerPage;
  const currentSenderIds = senderIds.slice(senderIdIndexOfFirstRecord, senderIdIndexOfLastRecord);
  const totalSenderIdPages = Math.ceil(senderIds.length / recordsPerPage);

  const domainIndexOfLastRecord = domainCurrentPage * recordsPerPage;
  const domainIndexOfFirstRecord = (domainCurrentPage - 1) * recordsPerPage;
  const currentDomains = domains.slice(domainIndexOfFirstRecord, domainIndexOfLastRecord);
  const totalDomainPages = Math.ceil(domains.length / recordsPerPage);

  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false);
  const [accountSettingsForm, setAccountSettingsForm] = useState({
    id: "",
    accountName: "",
    defaultDomain: "",
    language: "",
    location: "",
    urlTracking: false,
    individualTracking: false,
    isActive: true,
    logo: ""
  });
  const [accountSettingsErrors, setAccountSettingsErrors] = useState({
    language: "",
    location: "",
  });

  const validateLanguage = (value) => {
    const v = (value || "").trim();
    if (!v) return "Language is required";
    if (v.length < 2 || v.length > 50) return "Language must be 2-50 characters";
    const pattern = /^[A-Za-z\s-]+$/;
    if (!pattern.test(v)) return "Use letters, spaces, and hyphens only";
    return "";
  };

  const validateLocation = (value) => {
    const v = (value || "").trim();
    if (!v) return "Location is required";
    if (v.length < 2 || v.length > 100) return "Location must be 2-100 characters";
    const pattern = /^[A-Za-z0-9\s,.-]+$/;
    if (!pattern.test(v)) return "Use letters, numbers, spaces, commas, dots, hyphens";
    return "";
  };

  const [logoPreview, setLogoPreview] = useState(null);

  const handleAccountSettingsClick = async () => {
    await fetchDomains(selectedCompanyId);
    
    setAccountSettingsForm({
      id: selectedCompanyId,
      accountName: companyDetails.accountName || "", 
      defaultDomain: companyDetails.defaultDomain || "",
      language: companyDetails.language || "",
      location: companyDetails.location || "",
      urlTracking: companyDetails.urlTracking || false,
      individualTracking: companyDetails.individualTracking || false,
      isActive: companyDetails.isActive,
      logo: companyDetails.logo || ""
    });
    
    if (companyLogos[selectedCompanyId]) {
      setLogoPreview(companyLogos[selectedCompanyId]);
    } else {
      setLogoPreview(null);
    }
    setShowAccountSettingsModal(true);
  };

  const handleAccountSettingsFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "logoFile" && e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAccountSettingsForm({
        ...accountSettingsForm,
        logoFile: file
      });
      setLogoPreview(URL.createObjectURL(file));
      return;
    }
    
    setAccountSettingsForm({
      ...accountSettingsForm,
      [name]: type === "checkbox" ? checked : value,
    });

    if (name === "language") {
      setAccountSettingsErrors((prev) => ({
        ...prev,
        language: validateLanguage(value),
      }));
    } else if (name === "location") {
      setAccountSettingsErrors((prev) => ({
        ...prev,
        location: validateLocation(value),
      }));
    }
  };

  const saveAccountSettings = async () => {
    const errors = {
      language: validateLanguage(accountSettingsForm.language),
      location: validateLocation(accountSettingsForm.location),
    };
    setAccountSettingsErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      toast.error("Please fix the highlighted errors before saving");
      return;
    }
    try {
      let logoFileName = null;
      
      if (accountSettingsForm.logoFile instanceof File) {
        const formData = new FormData();
        formData.append('file', accountSettingsForm.logoFile);
        
        const uploadResponse = await accountService.uploadFile(formData);
        
        if (uploadResponse.success) {
          logoFileName = uploadResponse.data;
          
          const downloadResponse = await accountService.downloadLogo(logoFileName);
          
          if (downloadResponse) {
            const base64Image = `data:image/png;base64,${downloadResponse}`;
            setLogoPreview(base64Image);
            
            setCompanyLogos(prev => ({
              ...prev,
              [selectedCompanyId]: base64Image
            }));
          }
        }
      }

      const payload = {
        ...accountSettingsForm,
        logo: logoFileName || accountSettingsForm.logo
      };
      
      delete payload.logoFile;

      await accountService.updateAccountConfig(payload);

      setCompanyDetails(prev => ({
        ...prev,
        ...payload
      }));

      toast.success("Account settings updated successfully");
      setShowAccountSettingsModal(false);
      
      await fetchCompanyDetails(selectedCompanyId);
    } catch (error) {
      console.error("Failed to update account settings:", error);
      toast.error("Failed to update account settings");
    }
  };

  const [showDomainHelpModal, setShowDomainHelpModal] = useState(false);
  const [selectedDomainForHelp, setSelectedDomainForHelp] = useState(null);
  const [showDomainModal, setShowDomainModal] = useState(false);

  const handleSetDefaultDomain = async (domain) => {
    try {
      await domainService.setDefaultDomain(selectedCompanyId, domain.id);

  
      setCompanyDetails(prev => ({
        ...prev,
        defaultDomain: domain.domain
      }));
      
    
      await Promise.all([
        fetchCompanyDetails(selectedCompanyId),
        fetchDomains(selectedCompanyId)
      ]);
      
      toast.success("Default domain set successfully");
    } catch (error) {
      console.error("Error setting default domain:", error);
      toast.error("Failed to set default domain");
    }
  };

  const handleVerifyDomainNow = async (domain) => {
    try {
      setSelectedDomain(domain);
      const result = await domainService.checkDomain(selectedCompanyId, domain.domain);
      await fetchDomains(selectedCompanyId);
      if (result && result.message) {
        if (result.assigned) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } else {
        toast.success("Domain verification initiated");
      }
    } catch (error) {
      console.error("Error verifying domain:", error);
      toast.error(error.response?.data?.message || "Failed to verify domain");
    }
  };

  const [selectedProtocol, setSelectedProtocol] = useState('http://');

  return (
    <Container
      fluid
      className="ps-0 pb-4 pe-0  overflow-hidden mt-0 user-management-container bg-white"
    >
      {/* <Card className="card-nostyle">
        <Card.Body className="ps-0 pe-0">
          <div className="custom-domain-containers">
            <div
              className="d-flex justify-content-between ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
                <Image src={customDomainIcon} className="me-1" width="20" />{" "}
                Company Management
              </h4>
            </div>
          </div>
        </Card.Body>
      </Card> */}
      <ToastContainer position="top-right" autoClose={3000} />

      <Card className="card-nostyle border-0 shadow-none tab-card">
        <Card.Body className="ps-0 pe-0 pt-0">
          <Tab.Container
            activeKey={selectedCompanyId}
            onSelect={(k) => {
              if (!k || k === selectedCompanyId) return;
              setSelectedCompanyId(k);
              fetchCompanyUsers(k);
              fetchCompanySummary(k);
              fetchCompanyDetails(k);
            }}
          >
            <Row className="ps-0 pe-0">
              {!embedded && (
              <Col lg={4}>
                <CompanySidebar
                  companies={companies}
                  selectedCompanyId={selectedCompanyId}
                  onSelectCompany={(id) => {
                    if (!id || id === selectedCompanyId) return;
                    setSelectedCompanyId(id);
                    fetchCompanyUsers(id);
                    fetchCompanySummary(id);
                    fetchCompanyDetails(id);
                  }}
                  searchTerm={companySearchTerm}
                  onSearchTermChange={setCompanySearchTerm}
                  companyLogos={companyLogos}
                />
              </Col>
              )}

              <Col lg={embedded ? 12 : 8}>
                <Tab.Content className="cus-tab-content">
                  {isLoading ? (
                    <div className="text-center p-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading company data...</p>
                    </div>
                  ) : !dataFetched ? (
                    <div className="text-center p-5">
                      <p>No Previous Records!</p>
                    </div>
                  ) : (
                    companies.map((company) => (
                      <Tab.Pane key={company.id} eventKey={company.id}>
                        {selectedCompanyId === company.id && (
                          <>
                            <Card className="card-nostyle ps-0 pe-0">
                              <Card.Body className="pt-2 p-0">
                                <h6
                                  className="company-dark-bg-logo"
                                  style={{ fontSize: "13px" }}
                                >
                                  {companyDetails.accountName || ""}
                                </h6>

                                <div
                                  className="company-detail w-100 d-flex align-items-center gap-2 justify-content-between mb-2"
                                  style={{
                                    borderBottom: "1px solid rgb(228, 228, 228)",
                                    padding: "8px 8px 10px 8px",
                                  }}
                                >
                                  <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center gap-2">
                                      <Image
                                        width={40}
                                        height={40}
                                        className="me-2 rounded-circle object-containe"
                                        style={{ border: "1px solid #f4f4f4" }}
                                        src={
                                          companyLogos[selectedCompanyId] || companyImage
                                        }
                                        alt={companyDetails.accountName}
                                      />
                                      <div>
                                        <h6
                                          className="text-101828 ls-0 mb-0 text-capitalize fw-600"
                                          style={{ fontSize: "13px" }}
                                        >
                                          {companyDetails.accountName || ""}
                                        </h6>
                                        <div className="d-flex align-items-center gap-3">
                                          <p className="mb-0">
                                            <span className="text-muted fs-12">
                                              Location :{" "}
                                              {companyDetails.location || "N/A"}
                                            </span>
                                          </p>
                                          <p className="mb-0">
                                            <span className="text-muted fs-12">
                                              Language :{" "}
                                              {companyDetails.language || "N/A"}
                                            </span>
                                          </p>
                                          <p className="mb-0">
                                            <span
                                              className="text-muted fs-12"
                                              style={{
                                                width: "250px",

                                                overflow: "hidden",

                                                textOverflow: "ellipsis",

                                                position: "relative",

                                                display: "block",

                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              Default Domain :{" "}
                                              {companyDetails.defaultDomain || "N/A"}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className={`badge-custom badge-border-${
                                          companyDetails.isActive ? "success" : "danger"
                                        } badge badge-before position-relative ps-3 bg-${
                                          companyDetails.isActive ? "success" : "danger"
                                        }`}
                                      >
                                        {companyDetails.isActive ? "Active" : "Inactive"}
                                      </span>
                                      {userRole === "SUPERADMIN" && (
                                        <span
                                          className="badge-custom badge-border-success badge position-relative ps-3 bg-success d-flex align-items-center gap-1"
                                          onClick={handleAccountSettingsClick}
                                          title="Account Settings"
                                          style={{ cursor: "pointer" }}
                                        >
                                          <FaCog size={12} className="text-00C76F"/>
                                          <span className="fs-12"></span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div style={{ padding: "10px" }}>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fs-14 mb-0">Details</h6>
                                  </div>

                  <Form noValidate>
                    <Row>
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                            Available Credits
                          </Form.Label>
                                          <div className="border rounded bg-light p-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                              <span className="fs-12 text-muted">Shortening:</span>
                                              <span className="fs-14 fw-500">
                                                {(summaryData.availableCredit?.[0]?.shortening || 0).toLocaleString()}
                                              </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <span className="fs-12 text-muted">Tracking:</span>
                                              <span className="fs-14 fw-500">
                                                {(summaryData.availableCredit?.[1]?.tracking || 0).toLocaleString()}
                                              </span>
                                            </div>
                                          </div>
                                        </Form.Group>
                                      </Col>
                                      <Col lg={6}>
                                        <Form.Group className="mb-3">
                                          <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                                            Used Credits
                                          </Form.Label>
                                          <div className="border rounded bg-light p-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                              <span className="fs-12 text-muted">Shortening:</span>
                                              <span className="fs-14 fw-500">
                                                {(summaryData.usedCredit?.[0]?.shortening || 0).toLocaleString()}
                                              </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <span className="fs-12 text-muted">Tracking:</span>
                                              <span className="fs-14 fw-500">
                                                {(summaryData.usedCredit?.[1]?.tracking || 0).toLocaleString()}
                                              </span>
                                            </div>
                                          </div>
                                        </Form.Group>
                                      </Col>
                                    </Row>
                                  </Form>

                                  <div className="mt-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                      <p
                                        className={`mb-0 fs-12 ${
                                          activeTab === "users"
                                            ? "text-dark fw-bold text-success"
                                            : ""
                                        }`}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                          handleTabClick("users");
                                          setShowUsersTable(true);
                                        }}
                                      >
                                        <span className="text-black">Users{" "}</span>
                                        <span className="count_NUMBER ms-2">
                                          {summaryData.userCount}
                                        </span>
                                      </p>
                                      <p
                                        className={`mb-0 fs-12 ${
                                          activeTab === "senderId"
                                            ? "fw-bold text-success"
                                            : ""
                                        }`}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleTabClick("senderId")}
                                      >
                                        <span className="text-black">Sender ID</span>
                                        <span className="count_NUMBER ms-2">
                                          {summaryData.senderIdCount}
                                        </span>
                                      </p>
                                      <p
                                        className={`mb-0 fs-12 ${
                                          activeTab === "domain"
                                            ? "fw-bold text-success"
                                            : ""
                                        }`}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleTabClick("domain")}
                                      >
                                        <span className="text-black">Domain</span>
                                        <span className="count_NUMBER ms-2">
                                          {summaryData.domainCount}
                                        </span>
                                      </p>
                                    </div>

                                    {activeTab === "users" && showUsersTable && (
                                      <div className="mt-4">
                                        {isLoading ? (
                                          <div className="text-center">Loading...</div>
                                        ) : dataFetched && (!companyUsers || companyUsers.length === 0) ? (
                                          <div className="text-center pb-2">
                                            <Image width={100} src={noRecordFound} />
                                            <p className="fw-600 text-101828 mb-1 mt-2">
                                              No Previous Records!
                                            </p>
                                          </div>
                                        ) : (
                                          <>
                                            <Table responsive hover className="mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Sr No.</th>
                                                  <th>Username</th>
                                                  <th>Email</th>
                                                  <th>Role</th>
                                                  <th>Status</th>
                                                  <th>Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {currentRecords.map((user, index) => {
                                                  const status = getUserStatus(user);
                                                  return (
                                                    <tr key={user.id}>
                                                      <td>{indexOfFirstRecord + index + 1}</td>
                                                      <td>{user.username}</td>
                                                      <td style={{ width: "20%" }}>
                                                        <p
                                                          className="mb-0"
                                                          style={{
                                                            width: "100px",
                                                            overflow: "hidden",
                                                            overflowX: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                          }}
                                                          title={user.email}
                                                        >
                                                          {user.email}
                                                        </p>
                                                      </td>
                                                      <td>{user.roles?.join(", ")}</td>
                                                      <td>
                                                        <span
                                                          className={`badge bg-${
                                                            status === "Active"
                                                              ? "success"
                                                              : "danger"
                                                          }`}
                                                        >
                                                          {status}
                                                        </span>
                                                      </td>
                                                      <td>
                                                        <Dropdown className="cust-drop">
                                                          <Dropdown.Toggle
                                                            variant="link"
                                                            id="dropdown-basic"
                                                          >
                                                            <FaEllipsisV className="text-A8B0B9 fs-12" />
                                                          </Dropdown.Toggle>
                                                          <Dropdown.Menu>
                                                            <Dropdown.Item
                                                              href="#"
                                                              onClick={() =>
                                                                handleUserEditClick(user)
                                                              }
                                                              className="fs-12 pt-2 pb-2"
                                                            >
                                                              <GrEdit className="align-middle fs-12 me-1" />{" "}
                                                              Edit
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                              className="fs-12 pt-2 pb-2"
                                                              href="#"
                                                              onClick={() =>
                                                                handleUserDeleteClick(user)
                                                              }
                                                            >
                                                              <RiDeleteBin5Line className="align-middle fs-14 me-1" />{" "}
                                                              Delete
                                                            </Dropdown.Item>
                                                          </Dropdown.Menu>
                                                        </Dropdown>
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </Table>

                                            <div className="d-flex justify-content-center mt-4">
                                              <nav>
                                                <ul className="pagination mb-2">
                                                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handlePageChange(1)}
                                                      disabled={currentPage === 1}
                                                    >
                                                      &laquo;
                                                    </button>
                                                  </li>
                                                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handlePageChange(currentPage - 1)}
                                                      disabled={currentPage === 1}
                                                    >
                                                      &lt;
                                                    </button>
                                                  </li>
                                                  {(() => {
                                                    const startPage = Math.max(currentPage - 1, 1);
                                                    const endPage = Math.min(startPage + 2, totalPages);
                                                    const adjustedStartPage = endPage === totalPages
                                                      ? Math.max(endPage - 2, 1)
                                                      : startPage;

                                                    return [...Array(endPage - adjustedStartPage + 1)].map((_, idx) => (
                                                      <li
                                                        key={adjustedStartPage + idx}
                                                        className={`page-item ${
                                                          currentPage === adjustedStartPage + idx ? "active" : ""
                                                        }`}
                                                      >
                                                        <button
                                                          className="page-link"
                                                          onClick={() => handlePageChange(adjustedStartPage + idx)}
                                                        >
                                                          {adjustedStartPage + idx}
                                                        </button>
                                                      </li>
                                                    ));
                                                  })()}
                                                  <li
                                                    className={`page-item ${
                                                      currentPage === totalPages ? "disabled" : ""
                                                    }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handlePageChange(currentPage + 1)}
                                                      disabled={currentPage === totalPages}
                                                    >
                                                      &gt;
                                                    </button>
                                                  </li>
                                                  <li
                                                    className={`page-item ${
                                                      currentPage === totalPages ? "disabled" : ""
                                                    }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handlePageChange(totalPages)}
                                                      disabled={currentPage === totalPages}
                                                    >
                                                      &raquo;
                                                    </button>
                                                  </li>
                                                </ul>
                                              </nav>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}

                                    {activeTab === "senderId" && (
                                      <div className="mt-4">
                                        {userRole === "SUPERADMIN" && (
                                          <div className="d-flex justify-content-end mb-3">
                                            <Button
                                              variant="success"
                                              className="rounded-pill"
                                              onClick={() =>
                                                setShowAddSenderIdModal(true)
                                              }
                                            >
                                              <FontAwesomeIcon
                                                icon={faPlus}
                                                className="me-1"
                                              />
                                              Add Sender ID
                                            </Button>
                                          </div>
                                        )}
                                        {senderIds.length === 0 ? (
                                          <div className="text-center pb-2">
                                            <Image width={100} src={noRecordFound} />
                                            <p className="fw-600 text-101828 mb-1 mt-2">
                                              No Previous Records!
                                            </p>
                                            <p className="fs-10 fw-400 text-616161 mb-0">
                                              Start by adding Sender ID by clicking on top
                                              right corner
                                            </p>
                                          </div>
                                        ) : (
                                          <>
                                            <Table responsive hover className="mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Sr No</th>
                                                  <th>Sender ID</th>
                                                  <th className="text-end">Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {currentSenderIds.map((sender, index) => (
                                                  <tr key={sender.id}>
                                                    <td>{senderIdIndexOfFirstRecord + index + 1}</td>
                                                    <td>{sender.senderId}</td>
                                                    <td className="text-end">
                                                      <Dropdown className="cust-drop">
                                                        <Dropdown.Toggle
                                                          variant="link"
                                                          id="dropdown-basic"
                                                        >
                                                          <FaEllipsisV className="text-A8B0B9 fs-12" />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                          <Dropdown.Item
                                                            href="#"
                                                            onClick={() => {
                                                              setSelectedSenderId(sender);
                                                              setSenderIdEditForm({
                                                                senderId: sender.senderId,
                                                                isApproved: true,
                                                              });
                                                              setShowSenderIdEditModal(
                                                                true
                                                              );
                                                            }}
                                                            className="fs-12 pt-2 pb-2"
                                                          >
                                                            <GrEdit className="align-middle fs-12 me-1" />{" "}
                                                            Edit
                                                          </Dropdown.Item>
                                                          <Dropdown.Item
                                                            className="fs-12 pt-2 pb-2"
                                                            href="#"
                                                            onClick={() => {
                                                              setSelectedSenderId(sender);
                                                              setShowSenderIdDeleteModal(
                                                                true
                                                              );
                                                            }}
                                                          >
                                                            <RiDeleteBin5Line className="align-middle fs-14 me-1" />{" "}
                                                            Delete
                                                          </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                      </Dropdown>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                            
                                            <div className="d-flex justify-content-center mt-4">
                                              <nav>
                                                <ul className="pagination mb-2">
                                                  <li className={`page-item ${senderIdCurrentPage === 1 ? "disabled" : ""}`}>
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleSenderIdPageChange(1)}
                                                      disabled={senderIdCurrentPage === 1}
                                                    >
                                                      &laquo;
                                                    </button>
                                                  </li>
                                                  <li className={`page-item ${senderIdCurrentPage === 1 ? "disabled" : ""}`}>
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleSenderIdPageChange(senderIdCurrentPage - 1)}
                                                      disabled={senderIdCurrentPage === 1}
                                                    >
                                                      &lt;
                                                    </button>
                                                  </li>
                                                  {(() => {
                                                    const startPage = Math.max(senderIdCurrentPage - 1, 1);
                                                    const endPage = Math.min(startPage + 2, totalSenderIdPages);
                                                    const adjustedStartPage = endPage === totalSenderIdPages
                                                      ? Math.max(endPage - 2, 1)
                                                      : startPage;

                                                    return [...Array(endPage - adjustedStartPage + 1)].map((_, idx) => (
                                                      <li
                                                        key={adjustedStartPage + idx}
                                                        className={`page-item ${
                                                          senderIdCurrentPage === adjustedStartPage + idx ? "active" : ""
                                                        }`}
                                                      >
                                                        <button
                                                          className="page-link"
                                                          onClick={() => handleSenderIdPageChange(adjustedStartPage + idx)}
                                                        >
                                                          {adjustedStartPage + idx}
                                                        </button>
                                                      </li>
                                                    ));
                                                  })()}
                                                  <li
                                                    className={`page-item ${
                                                      senderIdCurrentPage === totalSenderIdPages ? "disabled" : ""
                                                    }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleSenderIdPageChange(senderIdCurrentPage + 1)}
                                                      disabled={senderIdCurrentPage === totalSenderIdPages}
                                                    >
                                                      &gt;
                                                    </button>
                                                  </li>
                                                  <li
                                                    className={`page-item ${
                                                      senderIdCurrentPage === totalSenderIdPages ? "disabled" : ""
                                                    }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleSenderIdPageChange(totalSenderIdPages)}
                                                      disabled={senderIdCurrentPage === totalSenderIdPages}
                                                    >
                                                      &raquo;
                                                    </button>
                                                  </li>
                                                </ul>
                                              </nav>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}

                                    {activeTab === "domain" && (
                                      <div className="mt-4">
                                        {userRole === "SUPERADMIN" && (
                                          <div className="d-flex justify-content-end mb-3">
                                            <Button
                                              variant="success"
                                              className="rounded-pill"
                                              onClick={() => setShowAddDomainModal(true)}
                                            >
                                              <FontAwesomeIcon
                                                icon={faPlus}
                                                className="me-1"
                                              />
                                              Add Domain
                                            </Button>
                                          </div>
                                        )}
                                        {domains.length === 0 ? (
                                          <div className="text-center pb-2">
                                            <Image width={100} src={noRecordFound} />
                                            <p className="fw-600 text-101828 mb-1 mt-2">
                                              No Previous Records!
                                            </p>
                                            <p className="fs-10 fw-400 text-616161 mb-0">
                                              Start by adding Domain by clicking on top
                                              right corner
                                            </p>
                                          </div>
                                        ) : (
                                          <>
                                            <Table responsive hover className="mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Sr No</th>
                                                  <th>Domain</th>
                                                  {userRole === "SUPERADMIN" && <th>Status</th>}
                                                  <th className="text-end">Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {currentDomains.map((domain, index) => (
                                                  <tr key={domain.id}>
                                                    <td>{domainIndexOfFirstRecord + index + 1}</td>
                                                    <td>
                                                      <div className="d-flex align-items-center">
                                                        {domain.domain}
                                                        {domain.defaultDomain && (
                                                          <span 
                                                            className="ms-2 badge bg-success" 
                                                            style={{ 
                                                              fontSize: '10px', 
                                                              padding: '2px 6px' 
                                                            }}
                                                          >
                                                            Default
                                                          </span>
                                                        )}
                                                      </div>
                                                    </td>
                                                    {userRole === "SUPERADMIN" && (
                                                      <td>
                                                        <span className={`badge bg-${domain.assigned ? 'success' : 'danger'}`}>
                                                          {domain.assigned ? 'Verified' : 'Not Verified'}
                                                        </span>
                                                      </td>
                                                    )}
                                                    <td className="text-end">
                                                      <Dropdown className="cust-drop">
                                                        <Dropdown.Toggle
                                                          variant="link"
                                                          id="dropdown-basic"
                                                        >
                                                          <FaEllipsisV className="text-A8B0B9 fs-12" />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                          <Dropdown.Item
                                                            href="#"
                                                            onClick={() => {
                                                              setSelectedDomain(domain);
                                                              setDomainEditForm({
                                                                domain: domain.domain,
                                                                isVerified: true,
                                                              });
                                                              setShowDomainEditModal(true);
                                                            }}
                                                            className="fs-12 pt-2 pb-2"
                                                          >
                                                            <GrEdit className="align-middle fs-12 me-1" />{" "}
                                                            Edit
                                                          </Dropdown.Item>
                                                          <Dropdown.Item
                                                            className="fs-12 pt-2 pb-2"
                                                            href="#"
                                                            onClick={() => {
                                                              setSelectedDomain(domain);
                                                              setShowDomainDeleteModal(
                                                                true
                                                              );
                                                            }}
                                                          >
                                                            <RiDeleteBin5Line className="align-middle fs-14 me-1" />{" "}
                                                            Delete
                                                          </Dropdown.Item>
                                                          <Dropdown.Item
                                                            className="fs-12 pt-2 pb-2"
                                                            href="#"
                                                            onClick={() => {
                                                              setSelectedDomainForHelp(domain);
                                                              setShowDomainHelpModal(true);
                                                            }}
                                                          >
                                                            <FaQuestionCircle className="me-1" />{" "}
                                                            Help
                                                          </Dropdown.Item>
                                                          <Dropdown.Item
                                                            className="fs-12 pt-2 pb-2"
                                                            href="#"
                                                            onClick={() => {
                                                              setSelectedDomain(domain);
                                                              handleSetDefaultDomain(domain);
                                                            }}
                                                          >
                                                            <FaStar className="me-1" />{" "}
                                                            Set as Default
                                                          </Dropdown.Item>
                                                          {userRole === "SUPERADMIN" && (
                                                            <Dropdown.Item
                                                              className="fs-12 pt-2 pb-2"
                                                              href="#"
                                                              onClick={() => handleVerifyDomainNow(domain)}
                                                            >
                                                              <FaCheckCircle className="me-1" /> Verify Now
                                                            </Dropdown.Item>
                                                          )}
                                                        </Dropdown.Menu>
                                                      </Dropdown>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>

                                            {/* Pagination for Domains */}
                                            <div className="d-flex justify-content-center mt-4">
                                              <nav>
                                                <ul className="pagination mb-2">
                                                  <li className={`page-item ${domainCurrentPage === 1 ? "disabled" : ""}`}>
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleDomainPageChange(1)}
                                                      disabled={domainCurrentPage === 1}
                                                    >
                                                      &laquo;
                                                    </button>
                                                  </li>
                                                  <li className={`page-item ${domainCurrentPage === 1 ? "disabled" : ""}`}>
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleDomainPageChange(domainCurrentPage - 1)}
                                                      disabled={domainCurrentPage === 1}
                                                    >
                                                      &lt;
                                                    </button>
                                                  </li>
                                                  {(() => {
                                                    const startPage = Math.max(domainCurrentPage - 1, 1);
                                                    const endPage = Math.min(startPage + 2, totalDomainPages);
                                                    const adjustedStartPage = endPage === totalDomainPages
                                                      ? Math.max(endPage - 2, 1)
                                                      : startPage;

                                                    return [...Array(endPage - adjustedStartPage + 1)].map((_, idx) => (
                                                      <li
                                                        key={adjustedStartPage + idx}
                                                        className={`page-item ${
                                                          domainCurrentPage === adjustedStartPage + idx ? "active" : ""
                                                        }`}
                                                      >
                                                        <button
                                                          className="page-link"
                                                          onClick={() => handleDomainPageChange(adjustedStartPage + idx)}
                                                        >
                                                          {adjustedStartPage + idx}
                                                        </button>
                                                      </li>
                                                    ));
                                                  })()}
                                                  <li
                                                    className={`page-item ${
                                                      domainCurrentPage === totalDomainPages ? "disabled" : ""
                                                    }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleDomainPageChange(domainCurrentPage + 1)}
                                                      disabled={domainCurrentPage === totalDomainPages}
                                                    >
                                                      &gt;
                                                    </button>
                                                  </li>
                                                  <li
                                                    className={`page-item ${
                                                      domainCurrentPage === totalDomainPages ? "disabled" : ""
                                                    }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() => handleDomainPageChange(totalDomainPages)}
                                                      disabled={domainCurrentPage === totalDomainPages}
                                                    >
                                                      &raquo;
                                                    </button>
                                                  </li>
                                                </ul>
                                              </nav>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </>
                        )}
                      </Tab.Pane>
                    ))
                  )}
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Card.Body>
      </Card>

      <Modal
        show={showCreateCompanyModal}
        onHide={() => setShowCreateCompanyModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Add Company
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form noValidate>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Company Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter company name"
                value={newCompanyData.accountName}
                onChange={(e) =>
                  setNewCompanyData({
                    ...newCompanyData,
                    accountName: e.target.value,
                  })
                }
                className=""
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Domains (comma-separated)
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter domains (e.g., https://example1.com,https://example2.com)"
                value={
                  Array.isArray(newCompanyData.domains)
                    ? newCompanyData.domains.join(",")
                    : newCompanyData.domains
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const domainsArray = inputValue
                    ? inputValue.split(",").map((domain) => domain.trim())
                    : [];
                  setNewCompanyData({
                    ...newCompanyData,
                    domains: domainsArray,
                  });
                }}
                className=""
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Default Domain
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Default Domain"
                value={newCompanyData.defaultDomain}
                onChange={(e) =>
                  setNewCompanyData({
                    ...newCompanyData,
                    defaultDomain: e.target.value,
                  })
                }
                className=""
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Sender IDs (comma-separated)
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter sender IDs (e.g., PARK123)"
                value={
                  Array.isArray(newCompanyData.senderIds)
                    ? newCompanyData.senderIds.join(",")
                    : ""
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const senderIdsArray = inputValue
                    ? inputValue.split(",").map((id) => id.trim())
                    : [];
                  setNewCompanyData({
                    ...newCompanyData,
                    senderIds: senderIdsArray,
                  });
                }}
                className=""
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Status
              </Form.Label>
              <Form.Select
                value={newCompanyData.isActive}
                onChange={(e) =>
                  setNewCompanyData({
                    ...newCompanyData,
                    isActive: e.target.value === "true",
                  })
                }
                className=""
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0  pt-0 p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="danger"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowCreateCompanyModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={handleCreateCompany}
              disabled={
                !newCompanyData.accountName ||
                !newCompanyData.domains.length ||
                !newCompanyData.defaultDomain
              }
            >
              Add Company
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="sm"
      >
        <Modal.Body className="text-center p-4">
          <h5 className="mb-3">Confirm Delete</h5>
          <p>Are you sure you want to delete this company?</p>
          {companyToDelete && (
            <p className="fw-bold">Company: {companyToDelete.accountName}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="secondary"
              className="flex-grow-1 rounded-pill"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-grow-1 rounded-pill"
              onClick={confirmDelete}
            >
              Yes, Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Edit Company
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          {companyToEdit && (
            <Form noValidate>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Company Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="accountName"
                  placeholder="Enter company name"
                  value={editFormData.accountName}
                  onChange={handleEditFormChange}
                  className=""
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Domains (comma-separated)
                </Form.Label>
                <Form.Control
                  type="text"
                  name="domains"
                  placeholder="Enter domains (e.g., http://localhost:1023,http://localhost:1024)"
                  value={
                    Array.isArray(editFormData.domains)
                      ? editFormData.domains.join(",")
                      : editFormData.domains
                  }
                  onChange={handleEditFormChange}
                  className=""
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Default Domain
                </Form.Label>
                <Form.Control
                  type="text"
                  name="defaultDomain"
                  placeholder="Default Domain"
                  value={editFormData.defaultDomain}
                  onChange={handleEditFormChange}
                  className=""
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Sender IDs (comma-separated)
                </Form.Label>
                <Form.Control
                  type="text"
                  name="senderIds"
                  placeholder="Enter sender IDs (e.g., ENFILD21,ENFILD22)"
                  value={
                    Array.isArray(editFormData.senderIds)
                      ? editFormData.senderIds.join(",")
                      : ""
                  }
                  onChange={handleEditFormChange}
                  className=""
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Status
                </Form.Label>
                <Form.Select
                  name="isActive"
                  value={editFormData.isActive}
                  onChange={handleEditFormChange}
                  className=""
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={saveEditChanges}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSenderIdEditModal}
        onHide={() => setShowSenderIdEditModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Edit Sender ID
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Sender ID <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={senderIdEditForm.senderId}
                onChange={(e) => {
                  setSenderIdEditForm({
                    ...senderIdEditForm,
                    senderId: e.target.value,
                  });
                  if (senderIdEditError) setSenderIdEditError("");
                }}
                maxLength={20}
                isInvalid={!!senderIdEditError}
              />
              {senderIdEditError ? (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                  {senderIdEditError}
                </div>
              ) : (
                <Form.Text className="text-muted">
                  3-20 characters, letters and numbers only
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="light"
              className="flex-grow-1"
              onClick={() => setShowSenderIdEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={handleSenderIdEdit}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSenderIdDeleteModal}
        onHide={() => setShowSenderIdDeleteModal(false)}
        centered
        size="sm"
      >
        <Modal.Body className="text-center p-4">
          <h5 className="mb-3">Confirm Delete</h5>
          <p>Are you sure you want to delete this sender ID?</p>
          {selectedSenderId && (
            <p className="fw-bold">Sender ID: {selectedSenderId.senderId}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="light"
              className="flex-grow-1"
              onClick={() => setShowSenderIdDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-grow-1"
              onClick={handleSenderIdDelete}
            >
              Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDomainEditModal}
        onHide={() => setShowDomainEditModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Edit Domain
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Domain <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={domainEditForm.domain}
                onChange={(e) => {
                  setDomainEditForm({
                    ...domainEditForm,
                    domain: e.target.value,
                  });
                  if (domainEditError) setDomainEditError("");
                }}
                maxLength={50}
                isInvalid={!!domainEditError}
              />
              {domainEditError ? (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                  {domainEditError}
                </div>
              ) : (
                <Form.Text className="text-muted">
                  Max 50 characters
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="light"
              className="flex-grow-1"
              onClick={() => setShowDomainEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={handleDomainEdit}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDomainDeleteModal}
        onHide={() => setShowDomainDeleteModal(false)}
        centered
        size="sm"
      >
        <Modal.Body className="text-center p-4">
          <h5 className="mb-3">Confirm Delete</h5>
          <p>Are you sure you want to delete this domain?</p>
          {selectedDomain && (
            <p className="fw-bold">Domain: {selectedDomain.domain}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="light"
              className="flex-grow-1"
              onClick={() => setShowDomainDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-grow-1"
              onClick={handleDomainDelete}
            >
              Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddDomainModal}
        onHide={() => setShowAddDomainModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Add Domain
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form noValidate>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Domain <span className="text-danger">*</span>
              </Form.Label>
              <div className="d-flex">
                <Form.Select 
                  className="w-auto me-2"
                  value={selectedProtocol}
                  onChange={(e) => setSelectedProtocol(e.target.value)}
                  style={{ minWidth: '100px' }}
                >
                  <option value="http://">http://</option>
                  <option value="https://">https://</option>
                </Form.Select>
                <Form.Control
                  type="text"
                  placeholder="Enter domain"
                  value={newDomain}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^(https?:\/\/)/, '');
                    setNewDomain(value);
                    if (domainError) setDomainError("");
                  }}
                  maxLength={43}
                  isInvalid={!!domainError}
                />
                {domainError ? (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                    {domainError}
                  </div>
                ) : (
                  <Form.Text className="text-muted">
                    Max 50 characters total (including protocol)
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="light"
              className="flex-grow-1"
              onClick={() => setShowAddDomainModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={handleAddDomain}
              disabled={!newDomain}
            >
              Add Domain
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddSenderIdModal}
        onHide={() => setShowAddSenderIdModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Add Sender ID
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form noValidate>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                Sender ID <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter sender ID"
                value={newSenderId}
                onChange={(e) => {
                  setNewSenderId(e.target.value);
                  if (senderIdError) setSenderIdError("");
                }}
                maxLength={20}
                isInvalid={!!senderIdError}
              />
              {senderIdError ? (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                  {senderIdError}
                </div>
              ) : (
                <Form.Text className="text-muted">
                  3-20 characters, letters and numbers only (no symbols)
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="light"
              className="flex-grow-1"
              onClick={() => setShowAddSenderIdModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={handleAddSenderId}
              disabled={!newSenderId}
            >
              Add Sender ID
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showUserEditModal}
        onHide={() => setShowUserEditModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Edit User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {userToEdit && (
            <Form noValidate>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Contact Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="contactNo"
                  placeholder="Enter contact number"
                  className="rounded-pill"
                  value={userEditFormData.contactNo}
                  onChange={handleUserEditFormChange}
                  maxLength={10}
                  isInvalid={!!userEditErrors.contactNo}
                />
                <Form.Control.Feedback type="invalid">
                  {userEditErrors.contactNo}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  className="rounded-pill"
                  value={userEditFormData.email}
                  onChange={handleUserEditFormChange}
                  isInvalid={!!userEditErrors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {userEditErrors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Role
                </Form.Label>
                <Form.Select
                  className="rounded-pill"
                  name="role"
                  value={userEditFormData.role}
                  onChange={handleUserEditFormChange}
                >
                  <option value="">Select role</option>
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowUserEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={saveUserEditChanges}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showUserDeleteModal}
        onHide={() => setShowUserDeleteModal(false)}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <h5 className="text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Confirm Delete
          </h5>
          <p className="fw-400 fs-12 mb-1">
            Are you sure you want to delete this user?
          </p>
          {userToDelete && (
            <p className="fw-600 mt-2 mb-0 fs-14">{userToDelete.username}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-none pt-0 p-3 border-0">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light br-radius-8p"
              className="flex-grow-1"
              onClick={() => setShowUserDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-grow-1"
              onClick={confirmUserDelete}
            >
              Yes, Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAccountSettingsModal}
        onHide={() => {
          setShowAccountSettingsModal(false);
          setLogoPreview(null);
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Account Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          <Form noValidate>
            {/* Logo Section */}
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Company Logo
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                
                <div className="flex-grow-1">
                <div className="position-relative" style={{ width: '60px', height: '60px' }}>
                  <div className="logo-container" style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid #E4E7EC',
                    padding: '4px'
                  }}>
                    <Image
                      src={logoPreview || companyLogos[selectedCompanyId] || companyImage}
                      alt="Company Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                                    <Form.Label className="mb-0" style={{ cursor: 'pointer' }}>
                                      <Button
                      variant="p-0"
                      style={{padding: '0px', position: 'absolute', bottom: '-4px', right: '-6px'}}
                      size="sm"
                      as="span"
                      className="d-flex align-items-center border-0"
                    >
                     
                            <span
                              className="btn btn-outline-success badge bg-success"
                              style={{
                                fontSize: "8px",
                                padding: "0",
                                cursor: "pointer",
                                width: "14px",
                                height: "14px",
                                lineHeight: "11px",
                              }}
                              title="Choose Logo"
                            >
                            <FontAwesomeIcon
                                style={{fontSize:'6px'}}
                                icon={faPencil}
                              />{" "} 
                            </span>

                    </Button>

                    <Form.Control
                      type="file"
                      name="logoFile"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAccountSettingsFormChange}
                    />
                  </Form.Label>
                </div>

                  <small className="text-muted d-block mt-1 fs-10">
                    Supported formats: PNG, JPG, JPEG.
                  </small>
                </div>
              </div>
            </Form.Group>

            {userRole !== "SUPERADMIN" && (
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                  Account Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="accountName"
                  placeholder="Enter account name"
                  value={accountSettingsForm.accountName}
                  onChange={handleAccountSettingsFormChange}
                  className=""
                />
              </Form.Group>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Default Domain
              </Form.Label>
              <Form.Select
                name="defaultDomain"
                value={accountSettingsForm.defaultDomain}
                onChange={handleAccountSettingsFormChange}
                className=""
              >
                <option value="">Select Default Domain</option>
                {domains.map((domain) => (
                  <option key={domain.id} value={domain.domain}>
                    {domain.domain}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                    Language
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="language"
                    placeholder="Enter language"
                    value={accountSettingsForm.language}
                    onChange={handleAccountSettingsFormChange}
                    className=""
                    isInvalid={!!accountSettingsErrors.language}
                  />
                  <Form.Control.Feedback type="invalid">
                    {accountSettingsErrors.language}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                    Location
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    placeholder="Enter location"
                    value={accountSettingsForm.location}
                    onChange={handleAccountSettingsFormChange}
                    className=""
                    isInvalid={!!accountSettingsErrors.location}
                  />
                  <Form.Control.Feedback type="invalid">
                    {accountSettingsErrors.location}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="urlTracking"
                    label="URL Tracking"
                    checked={accountSettingsForm.urlTracking}
                    onChange={handleAccountSettingsFormChange}
                    className="fs-12"
                  />
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="individualTracking"
                    label="Individual Tracking"
                    checked={accountSettingsForm.individualTracking}
                    onChange={handleAccountSettingsFormChange}
                    className="fs-12"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">
                Account Status
              </Form.Label>
              <Form.Select
                name="isActive"
                value={accountSettingsForm.isActive}
                onChange={handleAccountSettingsFormChange}
                className=""
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light"
              className="flex-grow-1 br-radius-8"
              onClick={() => {
                setShowAccountSettingsModal(false);
                setLogoPreview(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={saveAccountSettings}
            >
              Save Settings
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDomainHelpModal}
        onHide={() => setShowDomainHelpModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Domain Activation Guide
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="domain-help-content">
            <h6 className="mb-3">How to Activate Your Domain</h6>
            
            {selectedDomainForHelp && (
              <div className="domain-info mb-4">
                <p className="fw-500 mb-2">Selected Domain: <span className="text-success">{selectedDomainForHelp.domain}</span></p>
              </div>
            )}

            <div className="steps-container">
              <h6 className="mb-3">Follow these steps to activate your domain:</h6>
              
              <ol className="ps-3">
                <li className="mb-3">
                  <strong>Add DNS Records</strong>
                  <p className="text-muted mb-2">Add the following DNS records to your domain's DNS settings:</p>
                  <div className="bg-light p-3 rounded mb-2">
                    <p className="mb-1"><strong>Record Type:</strong> CNAME</p>
                    <p className="mb-1"><strong>Host:</strong> @</p>
                    <p className="mb-0"><strong>Points to:</strong> your-domain.comsensetechnologies.com</p>
                  </div>
                </li>

                <li className="mb-3">
                  <strong>Verify Domain Ownership</strong>
                  <p className="text-muted mb-2">Add a TXT record to verify domain ownership:</p>
                  <div className="bg-light p-3 rounded mb-2">
                    <p className="mb-1"><strong>Record Type:</strong> TXT</p>
                    <p className="mb-1"><strong>Host:</strong> @</p>
                    <p className="mb-0"><strong>Value:</strong> comsense-verification=your-domain</p>
                  </div>
                </li>

                <li className="mb-3">
                  <strong>Wait for Propagation</strong>
                  <p className="text-muted">DNS changes can take up to 24-48 hours to propagate globally. Please be patient during this time.</p>
                </li>

                <li>
                  <strong>Verification</strong>
                  <p className="text-muted">Once DNS propagation is complete, our system will automatically verify your domain. You'll receive an email confirmation when the domain is successfully activated.</p>
                </li>
              </ol>

              <div className="alert alert-info mt-4">
                <strong>Need Help?</strong>
                <p className="mb-0">If you need assistance with domain activation, please contact our support team at support@comsensetechnologies.com</p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-3">
          <div className="d-flex gap-3 w-100">
            <Button
              variant="success"
              className="flex-grow-1"
              onClick={() => setShowDomainHelpModal(false)}
            >
              Got It
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CompanyManagement;

